/**
 * Which shop the user is currently working in (BE-02, BE-02a, BE-02b, BE-16).
 *
 * WHAT THIS REPLACES
 *
 * Two files used to guess at this. storage/scope.js tried seven different field
 * names on the login response — `shop_id`, `shopId`, `shop`, `tenant_id`,
 * `tenantId`, `branch_id`, `branchId` — and fell back to the string 'unknown'
 * when none of them appeared. None of them ever appeared, because the login
 * response did not carry a shop at all. So every user in every shop shared the
 * 'unknown' bucket, and the draft isolation that scope.js exists to provide was
 * not actually in effect.
 *
 * The backend has now answered BE-02, and the answer changed the shape of the
 * question. A shop is not a property of the user — a user can work in SEVERAL
 * shops from one account (BE-02b). So there is no field on the login response
 * to read. The active shop is a CHOICE the user makes, out of the entitlement
 * list at GET /shops, and it is sent on every request as `X-Shop-Id`.
 *
 * WHY THE CHOICE MUST BE PERSISTED
 *
 * From API_REFERENCE §3: a user entitled to two or more shops who sends no
 * header gets reads spanning ALL their shops and a 422 on every write. So the
 * header is not an optimisation — without it, a salesperson at a two-shop
 * business cannot save an order at all. Losing the choice on refresh would mean
 * a blocked till, so it is written to localStorage rather than held in memory.
 *
 * WHY IT IS NOT IN REDUX
 *
 * The axios request interceptor needs it, and that runs outside React. Reading
 * it from a module that owns localStorage keeps one source of truth for both
 * the interceptor and the components, instead of a store the interceptor would
 * have to reach into sideways.
 */

const ACTIVE_SHOP_KEY = 'activeShopId'
const SHOPS_KEY = 'entitledShops'

/**
 * The shop id to stamp on requests, or null when we genuinely do not know.
 *
 * null is a meaningful answer and must not be papered over. It means "send no
 * header", which is CORRECT for a single-shop user — the backend infers their
 * only shop — and is the honest state before GET /shops has answered. Inventing
 * an id here would be worse than sending none: a wrong-but-entitled id files a
 * sale against the wrong shop's books, silently, and nothing errors.
 */
export function getActiveShopId() {
    try {
        const raw = localStorage.getItem(ACTIVE_SHOP_KEY)
        if (!raw || raw === 'null' || raw === 'undefined') return null
        return raw
    } catch (e) {
        // Private browsing, or storage disabled. Falling back to null means the
        // single-shop case keeps working and the multi-shop case fails loudly
        // at the API instead of quietly against the wrong shop.
        return null
    }
}

export function setActiveShopId(id) {
    try {
        if (id === null || id === undefined || id === '') {
            localStorage.removeItem(ACTIVE_SHOP_KEY)
            return
        }
        localStorage.setItem(ACTIVE_SHOP_KEY, String(id))
    } catch (e) {
        // See above — a shop that cannot be remembered is better than a crash
        // on the login path.
    }
}

/**
 * The shops this user may work in, as last returned by GET /shops.
 *
 * Cached because two things need it on every page: the picker in the header,
 * and the invoice, which prints the active shop's name and address (BE-16).
 * Neither should fire a request per render, and the invoice in particular is
 * often rendered straight into a print window where a pending fetch would show
 * a customer a receipt with a blank letterhead.
 */
export function getShops() {
    try {
        const raw = localStorage.getItem(SHOPS_KEY)
        if (!raw) return []
        const parsed = JSON.parse(raw)
        return Array.isArray(parsed) ? parsed : []
    } catch (e) {
        // Corrupt storage — a half-written value, a manual edit — must not take
        // the header down with it.
        return []
    }
}

export function setShops(shops) {
    try {
        localStorage.setItem(SHOPS_KEY, JSON.stringify(Array.isArray(shops) ? shops : []))
        localStorage.setItem(SHOPS_AT_KEY, String(Date.now()))
    } catch (e) {
        // Quota, or storage disabled. The picker falls back to re-fetching.
    }
}

const SHOPS_AT_KEY = 'entitledShopsAt'

/**
 * How stale the cached entitlement list is, in milliseconds.
 *
 * Infinity when we have never fetched one, which is what makes "refresh if
 * stale" fetch on a cold start without a separate flag for it.
 */
export function shopsAge() {
    try {
        const at = Number(localStorage.getItem(SHOPS_AT_KEY))
        if (!Number.isFinite(at) || at <= 0) return Infinity
        // A clock moved backwards would otherwise report a negative age and
        // pin the cache open forever.
        return Math.max(0, Date.now() - at)
    } catch (e) {
        return Infinity
    }
}

/** The full record for the active shop, or null. Used by the invoice header. */
export function getActiveShop() {
    const id = getActiveShopId()
    const shops = getShops()

    if (!id) {
        // A single-shop user never picks one, so there is no stored id — but
        // there is exactly one entitlement, and that is unambiguously the shop
        // they are in. Printing its name is correct; printing nothing is not.
        return shops.length === 1 ? shops[0] : null
    }

    return shops.find((shop) => String(shop?.id) === String(id)) || null
}

/**
 * Does this user have to choose?
 *
 * Drives whether the picker is shown at all. One shop is the common case and
 * gets no picker — a control with a single option is noise, and worse, it
 * implies a decision exists where none does.
 */
export function mustPickShop() {
    return getShops().length > 1
}

const SHOPS_KNOWN_KEY = 'entitledShopsFetched'

/**
 * Does this user belong to NO shop at all? (rule 1)
 *
 * Deliberately distinct from "we have not asked yet", and the distinction is
 * the entire point. Both look like an empty list from getShops(), but they mean
 * opposite things:
 *
 *   not asked yet   the ordinary state one tick after login. Treating it as
 *                   "no shop" would flash the locked-out banner at every user
 *                   on every cold start.
 *   asked, and []   a user the administrator has not finished setting up. They
 *                   get the catalogue and an explanation, and nothing else.
 *
 * So this is only true once GET /shops has actually come back empty.
 */
export function hasNoShop() {
    try {
        return localStorage.getItem(SHOPS_KNOWN_KEY) === '1' && getShops().length === 0
    } catch (e) {
        // Storage unavailable. Assume they have a shop: wrongly locking someone
        // out of the whole application is far worse than wrongly offering them
        // screens the API will refuse anyway.
        return false
    }
}

/**
 * Adopt a freshly fetched entitlement list.
 *
 * Returns the id that should now be active, so the caller can send it straight
 * back into the header without re-reading storage.
 *
 * The rule it enforces: an active shop that is no longer in the list is
 * DROPPED. Entitlements change — a salesperson moved between branches, an
 * admin revoked access — and a stale id would earn a 403 on every single
 * request with nothing on screen explaining why. Better to fall back to the
 * unambiguous case (one shop) or to no choice at all (many), and let the
 * picker ask again.
 */
export function reconcileShops(shops) {
    const list = Array.isArray(shops) ? shops : []
    setShops(list)

    try {
        // The server has now answered, so an empty list means "no shop"
        // rather than "not asked yet". See hasNoShop.
        localStorage.setItem(SHOPS_KNOWN_KEY, '1')
    } catch (e) {
        // Storage unavailable; hasNoShop falls back to assuming a shop exists.
    }

    const current = getActiveShopId()
    const stillEntitled = current && list.some((shop) => String(shop?.id) === String(current))

    if (stillEntitled) return current

    // Reaching here with a selection means it was REVOKED — the user was
    // removed from that shop while they were working in it.
    //
    // Dropping the id is not enough. Two caches still hold that shop's data and
    // neither self-heals: the stored permissions, which are the role they held
    // THERE, and the drafts, which are keyed by shop id. A half-finished
    // invoice for a branch they no longer work in resurfacing under another
    // shop's name is the exact failure storage/scope.js exists to prevent, and
    // its own header calls it "the single worst failure mode this system has".
    if (current) {
        forgetRevokedShop(current)
    }

    if (list.length === 1) {
        // Not strictly required — the backend infers a single-shop user's shop
        // from their account. Stamping it anyway makes every request explicit
        // and means the day a second shop is added, nothing about the request
        // shape changes.
        setActiveShopId(list[0].id)
        return String(list[0].id)
    }

    setActiveShopId(null)
    return null
}

/**
 * Wipe every trace of a shop the user is no longer entitled to.
 *
 * Three caches hold that shop's data, and only the first two are obvious:
 *
 *   the active id       dropped by the caller
 *   the permissions     the ROLE they held in that shop. Left alone, the
 *                       sidebar keeps offering that role's screens until the
 *                       next /me — every one of which the API now 403s.
 *   the drafts          keyed by shop id, and the reason this function exists
 *                       rather than being three lines inline.
 *
 * Imported lazily. storage/scope.js imports from this module to work out the
 * current shop, so a top-level import back into storage would be a cycle — and
 * under Jest a cycle here resolves to undefined rather than throwing, which
 * would make the purge silently do nothing.
 */
function forgetRevokedShop(shopId) {
    try {
        // eslint-disable-next-line global-require
        const { purgeShopScope } = require('../storage/scopedStorage')
        purgeShopScope(shopId)
    } catch (e) {
        // Never let cache cleanup break the login or layout path.
    }

    try {
        const raw = localStorage.getItem('userDetails')
        if (!raw) return

        const details = JSON.parse(raw)
        if (details && Array.isArray(details.permissions)) {
            // Emptied rather than deleted. hasPermission now denies on an empty
            // list, so this fails CLOSED for the moment between the revocation
            // being noticed and the next /me answering.
            details.permissions = []
            localStorage.setItem('userDetails', JSON.stringify(details))
        }
    } catch (e) {
        // Corrupt storage must not take the layout down with it.
    }
}

/**
 * Forget everything about shops.
 *
 * Called on logout. A counter PC is shared: leaving the previous user's shop
 * selected means the next person's first order is stamped with it unless they
 * notice the picker, and they will not notice the picker.
 */
export function clearActiveShop() {
    try {
        localStorage.removeItem(ACTIVE_SHOP_KEY)
        localStorage.removeItem(SHOPS_KEY)
        // Otherwise the next person's list looks freshly fetched and the
        // staleness check skips the one fetch that matters most.
        localStorage.removeItem(SHOPS_AT_KEY)
        // Otherwise the next person to log in on this machine is briefly
        // treated as having no shop, and is bounced to the catalogue with the
        // locked-out banner before their own /shops answers.
        localStorage.removeItem(SHOPS_KNOWN_KEY)
    } catch (e) {
        // localStorage.clear() on the logout path covers this anyway; this is
        // belt and braces for callers that clear only the shop.
    }
}
